import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useState } from 'react';
import { WordmarkToneProvider } from './brand.js';
import { parseThemePreference, readThemePreference } from './theme-preference.js';
const ThemeContext = createContext({ preference: 'system', theme: 'light', setPreference: () => { } });
export const useTheme = () => useContext(ThemeContext);
/** Each app entry point supplies its stable storage key. Storybook controls
 * the choice without changing the browser's saved production preference. */
export function ThemeProvider({ children, storageKey, preference: controlled, onPreferenceChange }) {
    const [saved, setSaved] = useState(() => readThemePreference(storageKey));
    const [systemDark, setSystemDark] = useState(() => window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false);
    const preference = controlled ?? saved;
    const theme = preference === 'system' ? (systemDark ? 'dark' : 'light') : preference;
    const setPreference = useCallback((value) => {
        if (controlled !== undefined) {
            onPreferenceChange?.(value);
            return;
        }
        setSaved(value);
        try {
            window.localStorage.setItem(storageKey, value);
        }
        catch { /* keep the in-memory choice */ }
    }, [controlled, onPreferenceChange, storageKey]);
    useEffect(() => {
        const media = window.matchMedia?.('(prefers-color-scheme: dark)');
        const changed = (event) => setSystemDark(event.matches);
        const stored = (event) => {
            if (event.key === storageKey || event.key === null)
                setSaved(parseThemePreference(event.newValue));
        };
        if (media)
            setSystemDark(media.matches);
        media?.addEventListener('change', changed);
        window.addEventListener('storage', stored);
        return () => {
            media?.removeEventListener('change', changed);
            window.removeEventListener('storage', stored);
        };
    }, [storageKey]);
    useLayoutEffect(() => {
        document.documentElement.dataset.colorTheme = theme;
        return () => { delete document.documentElement.dataset.colorTheme; };
    }, [theme]);
    return (_jsx(ThemeContext, { value: { preference, theme, setPreference }, children: _jsx(WordmarkToneProvider, { tone: theme === 'dark' ? 'reversed' : 'standard', children: children }) }));
}
//# sourceMappingURL=theme-provider.js.map